// src/components/SITAC/ElettrodottiLayer.tsx
// DOS518 – Layer Leaflet: linee elettrodotto + buffer di sicurezza + popup tensione
//
// Dipendenze da installare:
//   npm install @turf/turf
//   (Leaflet già presente nel progetto)

import { useEffect, useRef } from 'react';
import * as L from 'leaflet';
import * as turf from '@turf/turf';
import type { Feature, LineString } from 'geojson';
import {
  ElettrodottiGeoJSON,
  ElettrodottoFeature,
  ClasseTensione,
} from '../../utils/elettrodottiOverpass';

// ─── Props ────────────────────────────────────────────────────────────────────

interface ElettrodottiLayerProps {
  map: L.Map;
  data: ElettrodottiGeoJSON | null;
  visible: boolean;
}

// ─── Palette AIB ──────────────────────────────────────────────────────────────

const PALETTE: Record<ClasseTensione, { line: string; buffer: string; dashArray?: string }> = {
  AAT:        { line: '#CC0000', buffer: '#FF000030' },              // rosso vivo
  AT:         { line: '#E83A00', buffer: '#E83A0020' },              // rosso-arancio
  MT:         { line: '#FF6600', buffer: '#FF660018' },              // arancio
  BT:         { line: '#FF9900', buffer: '#FF990012', dashArray: '4,4' }, // giallo-arancio
  sconosciuta:{ line: '#FF0000', buffer: '#FF000020', dashArray: '6,3' }, // rosso tratteggiato
};

const LINE_WEIGHT: Record<ClasseTensione, number> = {
  AAT: 4, AT: 3, MT: 2.5, BT: 1.5, sconosciuta: 2,
};

// ─── Helper: icona fulmine ────────────────────────────────────────────────────

function fulminoIcon(color: string): L.DivIcon {
  return L.divIcon({
    html: `<span style="
      color:${color};
      font-size:13px;
      text-shadow:1px 1px 2px #fff,-1px -1px 2px #fff;
      line-height:1;
      display:block;
    ">⚡</span>`,
    className: '',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

// ─── Helper: punto medio di una polyline ─────────────────────────────────────

function midPoint(coords: [number, number][]): [number, number] {
  return coords[Math.floor(coords.length / 2)];
}

// ─── Helper: HTML popup ───────────────────────────────────────────────────────

const PERICOLO_LABEL: Record<ClasseTensione, string> = {
  AAT:        '🔴 PERICOLO ESTREMO – vietato sorvolo < 100 m',
  AT:         '🟠 PERICOLO ELEVATO – cautela elicotteri/Canadair',
  MT:         '🟡 Cautela mezzi aerei e terrestri',
  BT:         '🟢 Bassa tensione – rischio ridotto',
  sconosciuta:'⚠️ Tensione non classificata – buffer cautelativo',
};

function buildPopupHTML(f: ElettrodottoFeature): string {
  const p = f.properties;
  const kv = p.voltageKV > 0 ? `${Math.round(p.voltageKV)} kV` : 'n.d.';
  const pericolo = PERICOLO_LABEL[p.classe];

  return `
    <div style="font-family:system-ui,sans-serif;min-width:210px">
      <div style="
        background:#CC0000;color:#fff;
        padding:4px 8px;margin:-8px -12px 8px;
        font-weight:bold;font-size:12px;
        border-radius:3px 3px 0 0;
      ">⚡ ELETTRODOTTO</div>

      <table style="width:100%;border-collapse:collapse;font-size:12px">
        <tr>
          <td style="color:#666;padding:2px 0;width:45%">Classe:</td>
          <td><b style="color:#CC0000">${p.classe}</b></td>
        </tr>
        <tr>
          <td style="color:#666;padding:2px 0">Tensione:</td>
          <td><b>${kv}</b></td>
        </tr>
        ${p.cables ? `<tr><td style="color:#666;padding:2px 0">Cavi:</td><td>${p.cables}</td></tr>` : ''}
        ${p.name ? `<tr><td style="color:#666;padding:2px 0">Nome:</td><td>${p.name}</td></tr>` : ''}
        ${p.operator ? `<tr><td style="color:#666;padding:2px 0">Gestore:</td><td>${p.operator}</td></tr>` : ''}
        <tr>
          <td style="color:#666;padding:2px 0">Buffer:</td>
          <td><b style="color:#CC0000">${p.bufferMetri} m</b></td>
        </tr>
      </table>

      <div style="
        margin-top:8px;padding:5px 7px;
        background:#fff3f3;border-left:3px solid #CC0000;
        font-size:11px;line-height:1.4;
      ">${pericolo}</div>

      <div style="margin-top:4px;font-size:10px;color:#999">
        Sorgente: OpenStreetMap (${new Date().getFullYear()})
      </div>
    </div>
  `;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function ElettrodottiLayer({ map, data, visible }: ElettrodottiLayerProps) {
  const layerGroupRef = useRef<L.LayerGroup | null>(null);

  // Crea il group una sola volta
  useEffect(() => {
    layerGroupRef.current = L.layerGroup();
    // Ordine z-index: sotto i marker operativi ma sopra i tile
    layerGroupRef.current.addTo(map);
    return () => {
      layerGroupRef.current?.remove();
    };
  }, [map]);

  // Ridisegna quando cambiano dati o visibilità
  useEffect(() => {
    const group = layerGroupRef.current;
    if (!group) return;

    group.clearLayers();
    if (!visible || !data || data.features.length === 0) return;

    data.features.forEach((feature: ElettrodottoFeature) => {
      const { properties: p, geometry } = feature;
      const palette = PALETTE[p.classe] ?? PALETTE.sconosciuta;
      const weight  = LINE_WEIGHT[p.classe] ?? 2;

      // Coordinate: GeoJSON [lon,lat] → Leaflet [lat,lon]
      const latlngs: L.LatLngTuple[] = geometry.coordinates.map(
        ([lon, lat]) => [lat, lon]
      );

      // 1. Buffer poligono (via turf)
      try {
        const turfFeature = feature as unknown as Feature<LineString>;
        const buffered = turf.buffer(turfFeature, p.bufferMetri / 1000, { units: 'kilometers' });
        if (buffered?.geometry?.type === 'Polygon') {
          const bufCoords = (buffered.geometry.coordinates[0] as number[][]).map(
            ([lon, lat]) => [lat, lon] as L.LatLngTuple
          );
          L.polygon(bufCoords, {
            color: palette.line,
            fillColor: palette.buffer,
            fillOpacity: 1,
            weight: 0.5,
            opacity: 0.4,
            interactive: false,
            pane: 'overlayPane',
          }).addTo(group);
        }
      } catch {
        // Geometria degenere: skip buffer senza far esplodere il layer
      }

      // 2. Linea elettrodotto (cliccabile)
      const popupHTML = buildPopupHTML(feature);
      L.polyline(latlngs, {
        color: palette.line,
        weight,
        opacity: 0.9,
        dashArray: palette.dashArray,
        lineCap: 'round',
        lineJoin: 'round',
      })
        .bindPopup(popupHTML, { maxWidth: 300, className: 'elec-popup' })
        .addTo(group);

      // 3. Icona ⚡ al midpoint (non cliccabile, solo visiva)
      const [midLon, midLat] = midPoint(geometry.coordinates);
      L.marker([midLat, midLon], {
        icon: fulminoIcon(palette.line),
        interactive: false,
        zIndexOffset: 200,
        pane: 'markerPane',
      }).addTo(group);
    });
  }, [data, visible]);

  return null;
}
